import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Box,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  Pagination,
  IconButton,
  Stack,
  Avatar,
  MenuItem,
  Menu,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Autocomplete,
} from '@mui/material';
import {
  Search as SearchIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Close as CloseIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  Description as CsvIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../services/api';
import { User, Campaign, Department } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { format } from 'date-fns';
import moment from 'moment-timezone';
import { exportEmployeeAttendanceToCSV, exportEmployeeAttendanceToExcel, exportEmployeeAttendanceToPDF } from '../utils/exportUtils';
import { CampaignFilterService } from '../services/campaignFilterService';
import ShareReportDialog from '../components/ShareReportDialog';
import SearchableSelect from '../components/SearchableSelect';

interface ReportFilters {
  startDate: string;
  endDate: string;
  userId: string;
  departmentId: string;
  campaignId: string;
  page: number;
  limit: number;
}

interface ReportResponse {
  success: boolean;
  data: any[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

function calendarQuery(params: URLSearchParams, name: string): string {
  const value = String(params.get(name) || '').slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : '';
}

function normalizeEmployeeNumber(value: string | null | undefined): string {
  const raw = String(value || '').trim();
  if (!raw) return '';
  return raw.replace(/^0+/, '') || '0';
}

/** Dates and employee from a bonus proof link: ?startDate=&endDate=&employeeNumber=&userId= */
function attendanceFiltersFromLocation(): ReportFilters {
  const params = new URLSearchParams(window.location.search);
  const startDate = calendarQuery(params, 'startDate');
  const endDate = calendarQuery(params, 'endDate');
  return {
    startDate: startDate || moment().subtract(2, 'weeks').format('YYYY-MM-DD'),
    endDate: endDate || moment().format('YYYY-MM-DD'),
    userId: String(params.get('userId') || '').trim(),
    departmentId: '',
    campaignId: '',
    page: 1,
    limit: 25,
  };
}

const EmployeeAttendanceReportPage: React.FC = () => {
  const { user } = useAuth();
  const linkedEmployeeNumber = new URLSearchParams(window.location.search).get('employeeNumber');
  const [filters, setFilters] = useState<ReportFilters>(attendanceFiltersFromLocation);
  const [showFilters, setShowFilters] = useState(true);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [sortBy, setSortBy] = useState<'employee' | 'department' | 'status' | 'totalDays' | 'clockIns' | 'clockOuts' | 'avgWork' | 'totalWork' | 'attendance' | 'punctuality' | 'lastActivity'>('lastActivity');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc'); // default newest first

  // Check if user can share reports (supervisor and above)
  const canShareReports = ['supervisor', 'manager', 'administrator', 'super_user'].includes(user?.role || '');

  // Fetch users for filter dropdown
  const { data: usersData } = useQuery<{ data: { success: boolean; data: User[] } }, Error>({
    queryKey: ['users'],
    queryFn: apiClient.users.getAll,
    enabled: ['supervisor', 'manager', 'administrator', 'super_user'].includes(user?.role || ''),
  });

  // Fetch departments for filter dropdown
  const { data: departmentsData } = useQuery<{ data: { success: boolean; data: Department[] } }, Error>({
    queryKey: ['departments'],
    queryFn: apiClient.departments.getAll,
    enabled: ['supervisor', 'manager', 'administrator', 'super_user'].includes(user?.role || ''),
  });

  // Fetch campaigns for filter dropdown
  const { data: campaignsData } = useQuery<{ data: { success: boolean; data: Campaign[] } }, Error>({
    queryKey: ['campaigns', filters.startDate, filters.endDate],
    queryFn: () => apiClient.campaigns.getAll({
      includeWithActivityFrom: filters.startDate,
      includeWithActivityTo: filters.endDate,
    }),
    enabled: ['supervisor', 'manager', 'administrator', 'super_user'].includes(user?.role || ''),
  });

  // A bonus link names the employee by number. Select that person once the directory loads.
  useEffect(() => {
    if (!linkedEmployeeNumber || filters.userId) return;
    const users = usersData?.data?.data || [];
    if (!users.length) return;
    const target = normalizeEmployeeNumber(linkedEmployeeNumber);
    const match = users.find((person) => normalizeEmployeeNumber(person.employeeId) === target);
    if (!match?.id) return;
    setFilters((prev) => (prev.userId ? prev : { ...prev, userId: match.id, page: 1 }));
  }, [usersData, linkedEmployeeNumber, filters.userId]);

  // Check if user is an agent (hide filters for agents)
  const isAgent = user?.role === 'agent';

  // Filter departments and campaigns based on user role and assigned campaigns
  const availableDepartments = departmentsData?.data.data ? 
    (user && ['team_lead', 'supervisor'].includes(user.role) ? 
      departmentsData.data.data.filter(dept => dept.id === user.departmentId) :
      departmentsData.data.data) : [];

  const availableCampaigns = campaignsData?.data.data ? 
    (user ? CampaignFilterService.getUserAccessibleCampaigns(user, campaignsData.data.data) : []) : [];

  // Get report data
  const { data: reportResponse, isLoading, error, refetch } = useQuery<ReportResponse>({
    queryKey: ['employeeAttendanceReport', filters],
    queryFn: async () => {
      const response = await apiClient.timeEntries.getEmployeeAttendance(filters);
      return response.data;
    },
    enabled: true,
  });

  useEffect(() => {
    if (!filters.userId) return;
    document.querySelector('[data-attendance-focus="true"]')?.scrollIntoView({ block: 'center' });
  }, [reportResponse, filters.userId]);

  const handleFilterChange = (field: keyof ReportFilters, value: string | number) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      page: field !== 'page' ? 1 : (typeof value === 'number' ? value : prev.page),
    }));
  };

  const handleSearch = () => {
    refetch();
  };

  const handleResetFilters = () => {
    setFilters({
      startDate: moment().subtract(2, 'weeks').format('YYYY-MM-DD'),
      endDate: moment().format('YYYY-MM-DD'),
      userId: '',
      departmentId: '',
      campaignId: '',
      page: 1,
      limit: 25,
    });
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    handleFilterChange('page', value);
  };

  const getAttendanceColor = (rate: number) => {
    if (rate >= 90) return 'success';
    if (rate >= 70) return 'warning';
    return 'error';
  };

  const getPunctualityColor = (score: number) => {
    if (score >= 90) return 'success';
    if (score >= 70) return 'warning';
    return 'error';
  };

  const handleExportMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  const fetchAllEmployeeAttendanceForExport = async () => {
    const baseParams = {
      startDate: filters.startDate,
      endDate: filters.endDate,
      userId: filters.userId || undefined,
      departmentId: filters.departmentId || undefined,
      campaignId: filters.campaignId || undefined,
      page: 1,
      limit: 1000,
    };

    let allRecords: any[] = [];
    let page = 1;
    let total = 0;

    while (true) {
      const response = await apiClient.timeEntries.getEmployeeAttendance({
        ...baseParams,
        page
      });
      const pageData = response.data.data || [];
      const pagination = response.data.pagination;
      allRecords = allRecords.concat(pageData);
      total = pagination?.total ?? allRecords.length;

      if (allRecords.length >= total || pageData.length === 0) {
        break;
      }
      page += 1;
    }

    return allRecords;
  };

  const handleExportCSV = async () => {
    try {
      const data = await fetchAllEmployeeAttendanceForExport();
      exportEmployeeAttendanceToCSV(data, filters);
    } catch (error) {
      console.error('Error exporting employee attendance CSV:', error);
      alert('Failed to export CSV. Please try again.');
    } finally {
      handleExportMenuClose();
    }
  };

  const handleExportExcel = async () => {
    try {
      const data = await fetchAllEmployeeAttendanceForExport();
      exportEmployeeAttendanceToExcel(data, filters);
    } catch (error) {
      console.error('Error exporting employee attendance Excel:', error);
      alert('Failed to export Excel. Please try again.');
    } finally {
      handleExportMenuClose();
    }
  };

  const handleExportPDF = async () => {
    try {
      const data = await fetchAllEmployeeAttendanceForExport();
      exportEmployeeAttendanceToPDF(data, filters);
    } catch (error) {
      console.error('Error exporting employee attendance PDF:', error);
      alert('Failed to export PDF. Please try again.');
    } finally {
      handleExportMenuClose();
    }
  };

  const toggleSort = (col: 'employee' | 'department' | 'status' | 'totalDays' | 'clockIns' | 'clockOuts' | 'avgWork' | 'totalWork' | 'attendance' | 'punctuality' | 'lastActivity') => {
    if (sortBy === col) setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    else {
      setSortBy(col);
      setSortDir(col === 'lastActivity' ? 'desc' : 'asc');
    }
  };

  const getAssignmentStatusLabel = (status?: string) => {
    switch (status) {
      case 'absent':
        return 'Absent';
      case 'scheduled':
        return 'Scheduled';
      case 'unassigned':
        return 'Unassigned';
      default:
        return status || 'Unassigned';
    }
  };

  const getAssignmentStatusColor = (status?: string) => {
    switch (status) {
      case 'absent':
        return 'info';
      case 'scheduled':
        return 'success';
      case 'unassigned':
      default:
        return 'warning';
    }
  };

  const sortedEmployees = React.useMemo(() => {
    const list = reportResponse?.data ? [...reportResponse.data] : [];
    list.sort((a: any, b: any) => {
      let av: any = '';
      let bv: any = '';
      switch (sortBy) {
        case 'employee':
          av = `${a.user?.lastName || ''}${a.user?.firstName || ''}`.toLowerCase();
          bv = `${b.user?.lastName || ''}${b.user?.firstName || ''}`.toLowerCase();
          break;
        case 'department':
          av = (a.department || '').toLowerCase();
          bv = (b.department || '').toLowerCase();
          break;
        case 'status':
          av = (a.assignmentStatus || '').toString();
          bv = (b.assignmentStatus || '').toString();
          break;
        case 'totalDays':
          av = a.totalDays || 0; bv = b.totalDays || 0; break;
        case 'clockIns':
          av = a.totalClockIns || 0; bv = b.totalClockIns || 0; break;
        case 'clockOuts':
          av = a.totalClockOuts || 0; bv = b.totalClockOuts || 0; break;
        case 'avgWork':
          av = a.averageWorkTime || 0; bv = b.averageWorkTime || 0; break;
        case 'totalWork':
          av = a.totalWorkTime || 0; bv = b.totalWorkTime || 0; break;
        case 'attendance':
          av = a.attendanceRate || 0; bv = b.attendanceRate || 0; break;
        case 'punctuality':
          av = a.punctualityScore || 0; bv = b.punctualityScore || 0; break;
        case 'lastActivity':
        default:
          av = a.lastActivity ? new Date(a.lastActivity).getTime() : 0;
          bv = b.lastActivity ? new Date(b.lastActivity).getTime() : 0;
      }
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [reportResponse?.data, sortBy, sortDir]);

  const getWorkTimeColor = (hours: number) => {
    if (hours >= 8) return 'success';
    if (hours >= 6) return 'warning';
    return 'error';
  };

  const activeFilters = Object.entries(filters).filter(([key, value]) => 
    value !== '' && value !== 0 && key !== 'page' && key !== 'limit'
  );

  const sortIndicator = (col: typeof sortBy) => (sortBy === col ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');

  const headerCellSx = {
    px: 0.75,
    py: 1,
    fontSize: '0.72rem',
    fontWeight: 700,
    whiteSpace: 'nowrap' as const,
    lineHeight: 1.2,
    cursor: 'pointer',
  };

  const bodyCellSx = {
    px: 0.75,
    py: 0.75,
    fontSize: '0.78rem',
    verticalAlign: 'middle' as const,
  };

  const metricSx = (color: 'success' | 'warning' | 'error' | 'primary' | 'info' | 'default' = 'default') => ({
    fontWeight: 700,
    fontSize: '0.78rem',
    color:
      color === 'success' ? 'success.main'
      : color === 'warning' ? 'warning.main'
      : color === 'error' ? 'error.main'
      : color === 'primary' ? 'primary.main'
      : color === 'info' ? 'info.main'
      : 'text.primary',
  });

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4, px: { xs: 1.5, sm: 2 } }}>
      <Typography variant="h4" gutterBottom>
        Employee Attendance Report
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Filters</Typography>
            <IconButton onClick={() => setShowFilters(!showFilters)}>
              <FilterIcon />
            </IconButton>
          </Box>

          {showFilters && (
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Start Date"
                  type="date"
                  fullWidth
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="End Date"
                  type="date"
                  fullWidth
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {!isAgent && (
                <Grid item xs={12} sm={6} md={3}>
                  <Autocomplete
                    options={usersData?.data.data || []}
                    value={filters.userId ? usersData?.data.data.find((u: User) => u.id === filters.userId) || null : null}
                    onChange={(event, newValue) => {
                      handleFilterChange('userId', newValue?.id || '');
                    }}
                    getOptionLabel={(option) => option ? `${option.firstName} ${option.lastName} (${option.employeeId})` : ''}
                    renderInput={(params) => (
                      <TextField {...params} label="Employee" placeholder="Search employee..." />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        {option.firstName} {option.lastName} ({option.employeeId})
                      </li>
                    )}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    noOptionsText="No employees found"
                    clearOnEscape
                    selectOnFocus
                    handleHomeEndKeys
                    filterOptions={(options, { inputValue }) => {
                      if (!inputValue) return options;
                      const searchLower = inputValue.toLowerCase();
                      return options.filter(option =>
                        `${option.firstName} ${option.lastName}`.toLowerCase().includes(searchLower) ||
                        (option.employeeId && option.employeeId.toLowerCase().includes(searchLower))
                      );
                    }}
                  />
                </Grid>
              )}

              {!isAgent && (
                <Grid item xs={12} sm={6} md={3}>
                  <SearchableSelect
                    label="Department"
                    value={filters.departmentId}
                    onChange={(value) => handleFilterChange('departmentId', String(value))}
                    options={availableDepartments.map((d: Department) => ({
                      value: d.id,
                      label: d.name,
                    }))}
                  />
                </Grid>
              )}

              {!isAgent && (
                <Grid item xs={12} sm={6} md={3}>
                  <SearchableSelect
                    label="Campaign"
                    value={filters.campaignId}
                    onChange={(value) => handleFilterChange('campaignId', String(value))}
                    options={availableCampaigns.map((c: Campaign) => ({
                      value: c.id,
                      label: c.name,
                    }))}
                  />
                </Grid>
              )}

              <Grid item xs={12} sm={6} md={3}>
                <SearchableSelect
                  label="Records per page"
                  value={filters.limit}
                  onChange={(value) => handleFilterChange('limit', value as number)}
                  options={[10, 25, 50, 100].map((value) => ({
                    value,
                    label: String(value),
                  }))}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={handleResetFilters}
                  >
                    Reset
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<SearchIcon />}
                    onClick={handleSearch}
                    disabled={isLoading}
                  >
                    Search
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    endIcon={<ArrowDropDownIcon />}
                    onClick={handleExportMenuOpen}
                    disabled={!reportResponse?.data || reportResponse.data.length === 0}
                  >
                    Export
                  </Button>
                  {canShareReports && (
                    <Button
                      variant="outlined"
                      startIcon={<EmailIcon />}
                      onClick={() => setShareDialogOpen(true)}
                      disabled={!reportResponse?.data || reportResponse.data.length === 0}
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      Share
                    </Button>
                  )}
                </Stack>
              </Grid>
            </Grid>
          )}

          {activeFilters.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Active Filters:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {activeFilters.map(([key, value]) => (
                  <Chip
                    key={key}
                    label={`${key}: ${value}`}
                    onDelete={() => handleFilterChange(key as keyof ReportFilters, '')}
                    deleteIcon={<CloseIcon />}
                  />
                ))}
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Export Menu */}
      <Menu
        anchorEl={exportMenuAnchor}
        open={Boolean(exportMenuAnchor)}
        onClose={handleExportMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItem onClick={handleExportCSV}>
          <ListItemIcon>
            <CsvIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export to CSV</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleExportExcel}>
          <ListItemIcon>
            <ExcelIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export to Excel</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleExportPDF}>
          <ListItemIcon>
            <PdfIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export to PDF</ListItemText>
        </MenuItem>
      </Menu>

      {/* Results */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Employee Attendance
              {reportResponse?.pagination && (
                <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  ({reportResponse.pagination.total} total employees)
                </Typography>
              )}
            </Typography>
          </Box>

          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Failed to load employee attendance report
            </Alert>
          )}

          {reportResponse && !isLoading && (
            <>
              <TableContainer component={Paper} sx={{ maxHeight: 600, overflowX: 'hidden' }}>
                <Table stickyHeader size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...headerCellSx, width: '20%' }} onClick={() => toggleSort('employee')}>
                        <Tooltip title="Employee name and ID" arrow>
                          <span>Employee{sortIndicator('employee')}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ ...headerCellSx, width: '10%' }} onClick={() => toggleSort('department')}>
                        <Tooltip title="Department" arrow><span>Dept{sortIndicator('department')}</span></Tooltip>
                      </TableCell>
                      <TableCell sx={{ ...headerCellSx, width: '8%' }} onClick={() => toggleSort('status')}>
                        <Tooltip title="Assignment status" arrow><span>Status{sortIndicator('status')}</span></Tooltip>
                      </TableCell>
                      <TableCell align="center" sx={{ ...headerCellSx, width: '6%' }} onClick={() => toggleSort('totalDays')}>
                        <Tooltip title="Total days with activity" arrow><span>Days{sortIndicator('totalDays')}</span></Tooltip>
                      </TableCell>
                      <TableCell align="center" sx={{ ...headerCellSx, width: '6%' }} onClick={() => toggleSort('clockIns')}>
                        <Tooltip title="Total clock-ins" arrow><span>Ins{sortIndicator('clockIns')}</span></Tooltip>
                      </TableCell>
                      <TableCell align="center" sx={{ ...headerCellSx, width: '6%' }} onClick={() => toggleSort('clockOuts')}>
                        <Tooltip title="Total clock-outs" arrow><span>Outs{sortIndicator('clockOuts')}</span></Tooltip>
                      </TableCell>
                      <TableCell align="center" sx={{ ...headerCellSx, width: '7%' }} onClick={() => toggleSort('avgWork')}>
                        <Tooltip title="Average work time per day" arrow><span>Avg{sortIndicator('avgWork')}</span></Tooltip>
                      </TableCell>
                      <TableCell align="center" sx={{ ...headerCellSx, width: '8%' }} onClick={() => toggleSort('totalWork')}>
                        <Tooltip title="Total hours worked" arrow><span>Total{sortIndicator('totalWork')}</span></Tooltip>
                      </TableCell>
                      <TableCell align="center" sx={{ ...headerCellSx, width: '9%' }} onClick={() => toggleSort('attendance')}>
                        <Tooltip title="Attendance rate" arrow><span>Attend %{sortIndicator('attendance')}</span></Tooltip>
                      </TableCell>
                      <TableCell align="center" sx={{ ...headerCellSx, width: '9%' }} onClick={() => toggleSort('punctuality')}>
                        <Tooltip title="Punctuality score" arrow><span>Punct %{sortIndicator('punctuality')}</span></Tooltip>
                      </TableCell>
                      <TableCell sx={{ ...headerCellSx, width: '11%' }} onClick={() => toggleSort('lastActivity')}>
                        <Tooltip title="Last activity date and time" arrow><span>Last Activity{sortIndicator('lastActivity')}</span></Tooltip>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedEmployees.map((employee: any, index: number) => {
                      const pointed = Boolean(
                        filters.userId &&
                        (employee.user?.id === filters.userId ||
                          normalizeEmployeeNumber(employee.user?.employeeId) === normalizeEmployeeNumber(linkedEmployeeNumber)),
                      );
                      return (
                      <TableRow
                        key={employee.user?.id || index}
                        hover
                        selected={pointed}
                        data-attendance-focus={pointed ? 'true' : undefined}
                      >
                        <TableCell sx={bodyCellSx}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                            <Avatar sx={{ width: 28, height: 28, fontSize: '0.7rem', bgcolor: 'primary.main', flexShrink: 0 }}>
                              {employee.user?.firstName?.charAt(0)}{employee.user?.lastName?.charAt(0)}
                            </Avatar>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" fontWeight={600} noWrap sx={{ fontSize: '0.78rem', lineHeight: 1.2 }}>
                                {employee.user?.firstName} {employee.user?.lastName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.65rem', display: 'block' }}>
                                {employee.user?.employeeId} · {employee.user?.role}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={bodyCellSx}>
                          <Typography variant="caption" noWrap title={employee.department || 'N/A'} sx={{ fontSize: '0.72rem' }}>
                            {employee.department || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={bodyCellSx}>
                          <Chip
                            label={getAssignmentStatusLabel(employee.assignmentStatus)}
                            color={getAssignmentStatusColor(employee.assignmentStatus)}
                            size="small"
                            sx={{ height: 20, fontSize: '0.65rem', maxWidth: '100%', '& .MuiChip-label': { px: 0.75 } }}
                          />
                        </TableCell>
                        <TableCell align="center" sx={bodyCellSx}>
                          <Typography sx={metricSx('primary')}>{employee.totalDays}</Typography>
                        </TableCell>
                        <TableCell align="center" sx={bodyCellSx}>
                          <Typography sx={metricSx('success')}>{employee.totalClockIns}</Typography>
                        </TableCell>
                        <TableCell align="center" sx={bodyCellSx}>
                          <Typography sx={metricSx('error')}>{employee.totalClockOuts}</Typography>
                        </TableCell>
                        <TableCell align="center" sx={bodyCellSx}>
                          <Typography sx={metricSx(getWorkTimeColor(employee.averageWorkTime))}>
                            {employee.averageWorkTime}h
                          </Typography>
                        </TableCell>
                        <TableCell align="center" sx={bodyCellSx}>
                          <Tooltip
                            title={
                              <Box>
                                <Typography variant="caption" display="block" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                  Total Hours Worked:
                                </Typography>
                                <Typography variant="caption" display="block">
                                  {employee.totalWorkTime.toFixed(2)} hours
                                </Typography>
                                <Typography variant="caption" display="block" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                                  Sum of all work hours across {employee.totalDays} day{employee.totalDays !== 1 ? 's' : ''} in this period
                                </Typography>
                                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                  • Breaks and lunches are excluded
                                </Typography>
                                <Typography variant="caption" display="block">
                                  • Incomplete sessions are capped at end of day
                                </Typography>
                                <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'info.light' }}>
                                  Average: {employee.averageWorkTime.toFixed(2)}h per day
                                </Typography>
                              </Box>
                            }
                            arrow
                            placement="top"
                          >
                            <Typography
                              component="span"
                              sx={metricSx(getWorkTimeColor(employee.totalWorkTime / (employee.totalDays || 1)))}
                            >
                              {employee.totalWorkTime}h
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center" sx={bodyCellSx}>
                          <Tooltip
                            title={
                              <Box>
                                <Typography variant="caption" display="block" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                  Attendance Rate:
                                </Typography>
                                <Typography variant="caption" display="block">
                                  {employee.attendanceRate}%
                                </Typography>
                                <Typography variant="caption" display="block" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                                  Calculation: (Clock-Ins / Total Days) × 100
                                </Typography>
                                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                  • Total Clock-Ins: {employee.totalClockIns}
                                </Typography>
                                <Typography variant="caption" display="block">
                                  • Total Days: {employee.totalDays} (days with any time entries)
                                </Typography>
                                {employee.totalDays > 0 ? (
                                  <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'info.light' }}>
                                    {employee.totalClockIns} ÷ {employee.totalDays} = {employee.attendanceRate}%
                                  </Typography>
                                ) : (
                                  <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'warning.light' }}>
                                    No days with time entries found
                                  </Typography>
                                )}
                              </Box>
                            }
                            arrow
                            placement="top"
                          >
                            <Typography sx={metricSx(getAttendanceColor(employee.attendanceRate))}>
                              {employee.attendanceRate}%
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center" sx={bodyCellSx}>
                          <Tooltip
                            title={
                              employee.punctualityDetails ? (
                                <Box>
                                  <Typography variant="caption" display="block" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                    Punctuality Breakdown:
                                  </Typography>
                                  <Typography variant="caption" display="block">
                                    • Scheduled Days: {employee.punctualityDetails.totalScheduledDays}
                                  </Typography>
                                  <Typography variant="caption" display="block">
                                    • Punctual Days: {employee.punctualityDetails.punctualDays} (on time or up to 15 min late)
                                  </Typography>
                                  <Typography variant="caption" display="block">
                                    • Clocked In: {employee.punctualityDetails.scheduledDaysWithClockIn}
                                  </Typography>
                                  {employee.punctualityDetails.daysWithClockInButNotPunctual > 0 && (
                                    <Typography variant="caption" display="block" sx={{ color: 'warning.light' }}>
                                      • Late: {employee.punctualityDetails.daysWithClockInButNotPunctual} (late {'>'}15 min)
                                    </Typography>
                                  )}
                                  {employee.punctualityDetails.scheduledDaysWithoutClockIn > 0 && (
                                    <Typography variant="caption" display="block" sx={{ color: 'error.light' }}>
                                      • No Clock-In: {employee.punctualityDetails.scheduledDaysWithoutClockIn}
                                    </Typography>
                                  )}
                                  {employee.punctualityDetails.totalScheduledDays === 0 && (
                                    <Typography variant="caption" display="block" sx={{ color: 'info.light' }}>
                                      No schedules found for this period
                                    </Typography>
                                  )}
                                </Box>
                              ) : (
                                `Punctuality: ${employee.punctualityScore}%`
                              )
                            }
                            arrow
                            placement="top"
                          >
                            <Typography sx={metricSx(getPunctualityColor(employee.punctualityScore))}>
                              {employee.punctualityScore}%
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={bodyCellSx}>
                          <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.72rem' }}>
                            {employee.lastActivityFormatted ?
                              `${format(new Date(employee.lastActivity), 'MMM dd')}, ${employee.lastActivityFormatted}` :
                              'N/A'
                            }
                          </Typography>
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              {reportResponse.pagination.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination
                    count={reportResponse.pagination.totalPages}
                    page={reportResponse.pagination.page}
                    onChange={handlePageChange}
                    color="primary"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}
            </>
          )}

          {reportResponse && reportResponse.data.length === 0 && !isLoading && (
            <Alert severity="info">
              No employee attendance data found matching the selected criteria.
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Share Report Dialog */}
      {canShareReports && (
        <ShareReportDialog
          open={shareDialogOpen}
          onClose={() => setShareDialogOpen(false)}
          reportType="employee-attendance"
          reportFilters={filters}
          reportTitle="Employee Attendance Report"
          onShareSuccess={() => {
            // Optionally show a success message
            console.log('Report shared successfully');
          }}
        />
      )}
    </Container>
  );
};

export default EmployeeAttendanceReportPage;
